import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { Button, Dialog, EmptyState, Icon, Notice, Seitenzustand, StatusBadge, TextField, type IconName } from "../ui";
import { ICONS } from "../ui/Icon";
import { Card, FormGrid, PageHeader } from "../ui/Layout";
import { formatEuro, parseEuro } from "@shared/geld";
import { useKachelAendern, useKachelAnlegen, useKachelnVerwaltung, type KachelEintrag } from "./verkaeufe-api";
import { api, ApiFehler, rufe } from "../api/client";
import { useLaden, type LadenDaten } from "./api";
import type { HubStatus } from "../../worker/durable/LadenHub";
import { formatDatumZeit } from "@shared/zeit";
import type { LadenEinstellungen } from "@shared/schemas/laden";

interface SumupStatus {
  konfiguriert: boolean;
  verbunden: boolean;
  merchantCode: string | null;
  verbundenSeit: string | null;
  redirectUri: string;
  hub: HubStatus;
}

const MELDUNG: Record<string, { tone: "success" | "attention" | "error"; text: string }> = {
  verbunden: { tone: "success", text: "SumUp ist verbunden. Die Kasse ordnet Zahlungen ab jetzt automatisch zu." },
  konto_waehlen: { tone: "attention", text: "Dein SumUp-Login hat mehrere Händlerkonten. Bitte das Konto dieses Ladens wählen." },
  abgelehnt: { tone: "attention", text: "Die Verbindung wurde bei SumUp abgebrochen." },
  state_ungueltig: { tone: "error", text: "Die Rückkehr von SumUp war nicht gültig (abgelaufen). Bitte noch einmal verbinden." },
  kein_haendlerkonto: { tone: "error", text: "Dieser SumUp-Login hat kein Händlerkonto." },
  fehler: { tone: "error", text: "Verbinden hat nicht geklappt." },
};

export function EinstellungenKasse() {
  const laden = useLaden();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const sumupParam = params.get("sumup");
  const status = useQuery({ queryKey: ["laden", "sumup"], queryFn: () => rufe<SumupStatus>(api.api.sumup.status.$get()), refetchInterval: 15_000 });
  const konten = useQuery({ queryKey: ["laden", "sumup", "konten"], queryFn: () => rufe<{ konten: { merchantCode: string; name: string | null }[] }>(api.api.sumup.konten.$get()), enabled: sumupParam === "konto_waehlen" });
  const start = useMutation({ mutationFn: () => rufe<{ url: string }>(api.api.sumup.start.$post()), onSuccess: (d) => window.location.assign(d.url) });
  const trennen = useMutation({ mutationFn: () => rufe(api.api.sumup.trennen.$post()), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
  const konto = useMutation({ mutationFn: (merchantCode: string) => rufe(api.api.sumup.konto.$post({ json: { merchantCode } })), onSuccess: () => { setParams({}); void qc.invalidateQueries({ queryKey: ["laden"] }); } });
  const abfragen = useMutation({ mutationFn: () => rufe<HubStatus>(api.api.sumup["jetzt-abfragen"].$post()), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden", "sumup"] }) });
  const [fehler, setFehler] = useState<string | null>(null);

  const meldung = sumupParam ? MELDUNG[sumupParam] : null;
  const grund = params.get("grund");

  return (
    <>
      <PageHeader kicker="Einstellungen" title="Kasse und SumUp" sub="Verbindung zur SumUp-Kasse, Abgleich-Zeitfenster, Kassenverhalten" />
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}{grund ? ` (${grund})` : ""}</Notice> : null}
      {fehler ? <Notice tone="error">{fehler}</Notice> : null}
      <Seitenzustand query={status}>
        {(s) => (
          <Card title="SumUp" actions={s.verbunden ? <StatusBadge status={s.hub.sumup === "getrennt" ? "error" : s.hub.sumup === "pause" ? "attention" : "paid"}>{s.hub.sumup === "getrennt" ? "Getrennt" : s.hub.sumup === "pause" ? "Pause (429)" : "Verbunden"}</StatusBadge> : <StatusBadge status="neutral">Nicht verbunden</StatusBadge>}>
            {!s.konfiguriert ? (
              <Notice tone="attention" title="SumUp-App noch nicht hinterlegt">
                Der Anbieter muss die OAuth-App bei SumUp anlegen (Redirect-URI <span className="app-code">{s.redirectUri}</span>) und die Secrets <span className="app-code">SUMUP_CLIENT_ID</span> / <span className="app-code">SUMUP_CLIENT_SECRET</span> setzen.
              </Notice>
            ) : null}
            {sumupParam === "konto_waehlen" && konten.data?.konten.length ? (
              <div className="sc-col">
                <span className="sc-strong">Händlerkonto wählen</span>
                {konten.data.konten.map((k) => (
                  <Button key={k.merchantCode} variant="secondary" onClick={() => konto.mutate(k.merchantCode)} loading={konto.isPending}>
                    {k.name ?? k.merchantCode} ({k.merchantCode})
                  </Button>
                ))}
              </div>
            ) : null}
            {s.verbunden ? (
              <>
                <dl className="app-dl">
                  <dt>Händlercode</dt><dd className="app-code">{s.merchantCode}</dd>
                  <dt>Verbunden seit</dt><dd>{s.verbundenSeit ? formatDatumZeit(s.verbundenSeit) : "–"}</dd>
                  <dt>Letzte Abfrage</dt><dd>{s.hub.letzteAbfrageAt ? formatDatumZeit(s.hub.letzteAbfrageAt) : "noch keine"}</dd>
                  <dt>Abfragen/Minute</dt><dd>{s.hub.abfragenLetzteMinute} · 429 bisher: {s.hub.anzahl429}</dd>
                  <dt>Nächste Abfrage</dt><dd>{s.hub.naechsterAlarmAt ? formatDatumZeit(s.hub.naechsterAlarmAt) : "nicht geplant"} · offene Warenkörbe: {s.hub.offeneWarenkoerbe}</dd>
                  {s.hub.letzterFehler ? <><dt>Letzter Fehler</dt><dd>{s.hub.letzterFehler}</dd></> : null}
                </dl>
                <div className="sc-toolbar">
                  <Button variant="secondary" icon="undo-2" onClick={() => abfragen.mutate()} loading={abfragen.isPending}>Jetzt abfragen</Button>
                  <Button variant="secondary" onClick={() => start.mutate()} loading={start.isPending} disabled={!s.konfiguriert}>Neu verbinden</Button>
                  <Button variant="quiet" icon="log-out" onClick={() => confirm("SumUp trennen? Die Kasse erkennt dann keine Zahlungen mehr.") && trennen.mutate()} loading={trennen.isPending}>Verbindung trennen</Button>
                </div>
              </>
            ) : (
              <div className="sc-toolbar">
                <Button variant="primary" icon="wallet" onClick={() => { setFehler(null); start.mutate(undefined, { onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Start hat nicht geklappt.") }); }} loading={start.isPending} disabled={!s.konfiguriert}>
                  Mit SumUp verbinden
                </Button>
                <span className="app-help">Anmeldung über SumUp, es wird kein Passwort gespeichert. Scopes: Transaktionen lesen, Profil lesen.</span>
              </div>
            )}
          </Card>
        )}
      </Seitenzustand>
      <Seitenzustand query={laden}>{(d) => <AbgleichFormular daten={d} />}</Seitenzustand>
      <LadenwareKacheln />
    </>
  );
}

// --- Ladenware-Kacheln (Frage 5) -----------------------------------------------------------------

const KACHEL_ICONS: IconName[] = ["store", "shopping-basket", "package", "euro", "tag", "sparkles", "receipt", "banknote"];

function LadenwareKacheln() {
  const kacheln = useKachelnVerwaltung();
  const anlegen = useKachelAnlegen();
  const aendern = useKachelAendern();
  const [bearbeiten, setBearbeiten] = useState<Partial<KachelEintrag> | null>(null);
  const [form, setForm] = useState({ name: "", preis: "", frei: false, icon: "store" });
  const [fehler, setFehler] = useState<string | null>(null);

  function oeffnen(k?: KachelEintrag) {
    setFehler(null);
    setBearbeiten(k ?? {});
    setForm({ name: k?.name ?? "", preis: k?.preisCent ? (k.preisCent / 100).toFixed(2).replace(".", ",") : "", frei: k ? k.preisCent === null : false, icon: k?.icon ?? "store" });
  }

  async function speichern() {
    const preisCent = form.frei ? null : parseEuro(form.preis);
    if (!form.name.trim()) return setFehler("Bitte einen Namen eingeben.");
    if (!form.frei && (!preisCent || preisCent <= 0)) return setFehler("Preis eingeben – oder „Betrag frei wählen“ ankreuzen.");
    setFehler(null);
    try {
      if (bearbeiten?.id) await aendern.mutateAsync({ id: bearbeiten.id, name: form.name.trim(), preisCent, icon: form.icon, reihenfolge: bearbeiten.reihenfolge ?? 0, aktiv: bearbeiten.aktiv ?? true });
      else await anlegen.mutateAsync({ name: form.name.trim(), preisCent, icon: form.icon, reihenfolge: kacheln.data?.kacheln.length ?? 0 });
      setBearbeiten(null);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Speichern hat nicht geklappt.");
    }
  }

  return (
    <Card title="Ladenware-Kacheln" actions={<Button variant="secondary" icon="plus" onClick={() => oeffnen()}>Kachel anlegen</Button>}>
      <p className="sc-muted">Eigene Artikel des Ladens (Tragetasche, Kaffee, Gutschein …) als Kacheln an der Kasse. Der Erlös geht an den Laden, nicht an einen Mieter.</p>
      <Seitenzustand query={kacheln} lines={2} istLeer={(d) => d.kacheln.length === 0} leer={<EmptyState compact title="Noch keine Kacheln">An der Kasse gibt es Ladenware erst, wenn hier eine Kachel angelegt ist.</EmptyState>}>
        {(d) => (
          <div className="sc-tiles">
            {d.kacheln.map((k) => (
              <button key={k.id} type="button" className="sc-tile" onClick={() => oeffnen(k)} style={k.aktiv ? undefined : { opacity: 0.55 }}>
                <span className="sc-row" style={{ justifyContent: "space-between", width: "100%" }}>
                  <Icon name={(k.icon in ICONS ? k.icon : "store") as IconName} size={24} />
                  {!k.aktiv ? <StatusBadge status="neutral">Ausgeblendet</StatusBadge> : null}
                </span>
                <span className="sc-col" style={{ gap: 0 }}>
                  <span className="sc-strong">{k.name}</span>
                  <span className="sc-muted">{k.preisCent === null ? "Betrag frei wählen" : formatEuro(k.preisCent)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Seitenzustand>
      <Dialog
        open={!!bearbeiten}
        title={bearbeiten?.id ? "Kachel bearbeiten" : "Kachel anlegen"}
        size="sm"
        onClose={() => setBearbeiten(null)}
        actions={
          <>
            {bearbeiten?.id ? (
              <Button variant="quiet" onClick={() => void aendern.mutateAsync({ id: bearbeiten.id!, name: bearbeiten.name!, preisCent: bearbeiten.preisCent ?? null, icon: bearbeiten.icon ?? "store", reihenfolge: bearbeiten.reihenfolge ?? 0, aktiv: !bearbeiten.aktiv }).then(() => setBearbeiten(null))} loading={aendern.isPending}>
                {bearbeiten.aktiv ? "Ausblenden" : "Einblenden"}
              </Button>
            ) : null}
            <span className="sc-grow" />
            <Button variant="quiet" onClick={() => setBearbeiten(null)}>Abbrechen</Button>
            <Button variant="primary" icon="check" loading={anlegen.isPending || aendern.isPending} onClick={() => void speichern()}>Speichern</Button>
          </>
        }
      >
        <div className="app-form">
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={40} autoFocus placeholder="z. B. Tragetasche" />
          <TextField label="Preis" value={form.preis} onChange={(e) => setForm({ ...form, preis: e.target.value })} inputMode="decimal" suffix="€" placeholder="0,00" disabled={form.frei} />
          <label className="sc-check"><input type="checkbox" checked={form.frei} onChange={(e) => setForm({ ...form, frei: e.target.checked })} /> Betrag frei wählen (an der Kasse eintippen)</label>
          <div className="sc-col" style={{ gap: 6 }}>
            <span className="rg-field__label">Symbol</span>
            <div className="sc-toolbar">
              {KACHEL_ICONS.map((i) => (
                <button key={i} type="button" className="sc-pill" aria-pressed={form.icon === i} aria-label={i} onClick={() => setForm({ ...form, icon: i })}>
                  <Icon name={i} size={20} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </Dialog>
    </Card>
  );
}

function AbgleichFormular({ daten }: { daten: LadenDaten }) {
  const qc = useQueryClient();
  const e = daten.einstellungen;
  const [form, setForm] = useState({ vorlauf: String(e.abgleich.vorlaufSekunden), nachlauf: String(e.abgleich.nachlaufMinuten), sperre: String(e.kasse.sperreNachMinuten), scanTon: e.kasse.scanTon, rabattFreigabe: String(e.kasse.rabattFreigabeAbProzent) });
  const speichern = useMutation({
    mutationFn: (neu: LadenEinstellungen) => rufe(api.api.laden.einstellungen.$put({ json: neu })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }),
  });
  const [erfolg, setErfolg] = useState(false);
  useEffect(() => {
    if (!erfolg) return;
    const t = setTimeout(() => setErfolg(false), 4000);
    return () => clearTimeout(t);
  }, [erfolg]);

  function absenden(ev: React.FormEvent) {
    ev.preventDefault();
    const neu: LadenEinstellungen = {
      ...e,
      abgleich: { vorlaufSekunden: Math.max(0, Math.min(300, Number(form.vorlauf) || 0)), nachlaufMinuten: Math.max(1, Math.min(120, Number(form.nachlauf) || 15)) },
      kasse: { ...e.kasse, sperreNachMinuten: Math.max(1, Math.min(60, Number(form.sperre) || 5)), scanTon: form.scanTon, rabattFreigabeAbProzent: Math.max(0, Math.min(100, Number(form.rabattFreigabe) || 0)) },
    };
    speichern.mutate(neu, { onSuccess: () => setErfolg(true) });
  }

  return (
    <form className="app-form" onSubmit={absenden} noValidate>
      {erfolg ? <Notice tone="success" title="Gespeichert">Die Änderung steht im Änderungsprotokoll.</Notice> : null}
      {speichern.isError ? <Notice tone="error">{speichern.error instanceof ApiFehler ? speichern.error.message : "Speichern hat nicht geklappt."}</Notice> : null}
      <Card title="Abgleich-Zeitfenster">
        <p className="sc-muted">Eine SumUp-Zahlung wird einem Warenkorb zugeordnet, wenn Betrag und Zeit passen. Ein längerer Vorlauf hat im Test einen früheren, fremden Verkauf erfasst.</p>
        <FormGrid>
          <TextField label="Vor dem Tippen auf Fertig" value={form.vorlauf} onChange={(ev) => setForm({ ...form, vorlauf: ev.target.value })} inputMode="numeric" suffix="Sekunden" hint="Standard 15" />
          <TextField label="Nach dem Tippen auf Fertig" value={form.nachlauf} onChange={(ev) => setForm({ ...form, nachlauf: ev.target.value })} inputMode="numeric" suffix="Minuten" hint="Standard 15" />
        </FormGrid>
      </Card>
      <Card title="Kasse">
        <FormGrid>
          <TextField label="Kasse sperren nach" value={form.sperre} onChange={(ev) => setForm({ ...form, sperre: ev.target.value })} inputMode="numeric" suffix="Minuten ohne Eingabe" />
          <TextField label="Rabatt-Freigabe ab" value={form.rabattFreigabe} onChange={(ev) => setForm({ ...form, rabattFreigabe: ev.target.value })} inputMode="numeric" suffix="%" hint="Darüber bestätigt ein Inhaber per PIN (Etappe 2)" />
        </FormGrid>
        <label className="sc-check"><input type="checkbox" checked={form.scanTon} onChange={(ev) => setForm({ ...form, scanTon: ev.target.checked })} /> Ton beim Scannen</label>
      </Card>
      <div className="app-form__actions">
        <Button type="submit" variant="primary" icon="check" loading={speichern.isPending}>Speichern</Button>
      </div>
    </form>
  );
}
